/**
 * @file ContentArchive.tsx
 * @description Component for displaying the archive of generated contents that are not part of any editorial plan.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast } from "react-toastify";

import config from "../../config/config";
import { auth } from "../../config/firebaseConfig";
import log from "../../config/logging";

import Loading from "../../components/common/Loading";
import ContentItem from "../../components/common/ContentItem";
import AlphabetBackground from "../../components/common/AlphabetBackground";
import ContentIcon from "../../components/common/icons/ContentIcon";
import { GeneratedContentDetails } from "../../types";

const ContentArchive: React.FC = () => {
  const [user, loading] = useAuthState(auth);
  const [contents, setContents] = useState<GeneratedContentDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const handleDownloadStatusChange = (id: string, downloaded: boolean) => {
    setContents((prevContents) =>
      prevContents.map((content) =>
        content.id === id ? { ...content, downloaded } : content
      )
    );
  };

  useEffect(() => {
    const fetchContents = async () => {
      if (loading) return;

      if (!user) {
        log.warn("User not authenticated, redirecting to login");
        toast.error("Per favore, accedi per visualizzare questa pagina");
        navigate("/accedi");
        return;
      }

      try {
        const token = await user.getIdToken(true);
        const response = await fetch(
          `${config.backendUrl}/api/contents?userId=${user.uid}&planId=null`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Errore: ${response.statusText}`);
        }

        const data = await response.json();
        log.debug("Contents fetched successfully", { data });

        setContents(data.contents);
      } catch (error: unknown) {
        log.error("Error fetching contents", { error });
        toast.error("Errore durante il recupero dei contenuti generati");
      } finally {
        setIsLoading(false);
      }
    };

    fetchContents();
  }, [user, loading, navigate]);

  if (loading || isLoading) {
    return <Loading />;
  }

  return (
    <section className="mx-auto max-w-content">
      <AlphabetBackground fill="#e7dddc" opacity={0.5} />
      <ContentIcon className="relative mx-auto mb-2 w-28" />
      <h2 className="text-2xl !font-medium text-primary !mb-20 text-center relative">
        Archivio contenuti
      </h2>
      <ul className="relative flex flex-col gap-4">
        {contents.map((contentDetail) => (
          <li
            key={contentDetail.id}
            className="flex items-start justify-between w-full gap-8 p-8 mb-4 xl:gap-12 rounded-3xl bg-base-100"
          >
            <ContentItem
              contentDetail={contentDetail}
              onDownloadStatusChange={handleDownloadStatusChange}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};

export default ContentArchive;
